import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { AppService } from "src/app/app.service";
import { BrokerItem } from "src/app/shared/source-name-table-display/source-name-table-dsiplay.model";
import { NgModel } from "../custom-consensus-core/custom-consensus-core.component";
import { CcVars } from "../custom-consensus.model";
import { CustomConsensusService } from "../custom-consensus.service";
import { VAIconRegistryService, close } from "@va/core-library/icons";

@Component({
  selector: "app-custom-consensus-modal",
  templateUrl: "./custom-consensus-modal.component.html",
  styleUrls: ["./custom-consensus-modal.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CustomConsensusModalComponent implements OnInit {
  isModalOpen$;
  ccVars: CcVars;

  constructor(
    private ccService: CustomConsensusService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([close]);
  }

  ngOnInit() {
    this.isModalOpen$ = this.ccService.isCcModalOpen$;

    //subscribe to MEDataSet event.
    window.addEventListener("MEDataSet", (rpObj: any) => {
      //When all medata is set we can extract ccVars data and pass it to custom consensus core
      //note: "ccUpdated" event is accepted in addition to "All" because at that time
      //all other paneldata was already set previously and only source is updated
      //we originally ony need source and revision panel data :)
      const sectionName = rpObj.detail.section;
      if (
        sectionName &&
        !["All", "ccUpdated", "Sources"].includes(sectionName)
      ) {
        return;
      }

      this.ccVars = this.ccService.getCcVarsForWeb();

      //check whether no broker is available for selected date range and show modal if necessary
      this.ccService.shouldShowCcNodataModal();
    });
  }

  /**
   * fired on receiving onSave event from custom consensus core component
   * which is emitted on hitting 'save' on cc core
   * @param prefsFromModal user selected settings on cc core
   */
  handleSave = (prefsFromModal: NgModel) => {
    //feature logging
    this.logCustomConsensusApply();

    //logging relative days on value change is an option.
    //but to prevent too many logs as the user is typing. Here logging once when saving.
    this.logRelativeDays(prefsFromModal);

    //write user settings on window.MEData
    this.updateBrokerlistOnMeData(prefsFromModal);
    this.updateRestOnMeData(prefsFromModal);

    //dispatch "MEDataSet".
    //This is listened in right-panel-source component where gridpanelhistory is fired with correct object
    window.dispatchEvent(
      new CustomEvent("MEDataSet", {
        detail: {
          data: window.MEData,
          section: "ccUpdated",
          clickEvent: event,
        },
      }),
    );

    //only for closing.
    this.ccService.showCcModal(false);
  };

  //eslint-disable-next-line
  handleCancel(prefsFromModal?) {
    this.ccService.showCcModal(false);
    this.ccVars = this.ccService.getCcVarsForWeb();
  }

  /**
   * writes/updates user's broker selection settings onto MEData object
   *
   * @param prefs user's selected settings for custom consensus
   */
  updateBrokerlistOnMeData = (prefs: NgModel) => {
    const {
      reaffirmBrokers,
      selectedBrokersByGrid,
      selectedBrokersByDate,
      hideNonCcBrokersInGrid,
      customConsensusMode,
    } = prefs;

    const panels = window.MEData.panels;
    const sourcePanel = panels.find((p) => p.name.toLowerCase() === "sources");
    const sourceTypes = sourcePanel.values;
    const brokers = sourceTypes.find((s) => s.name === "Brokers").values;
    sourcePanel.exclude_reaffirm_sources = reaffirmBrokers;
    let selectedBrokers = selectedBrokersByGrid;
    if (customConsensusMode === "byDate") {
      selectedBrokers = selectedBrokersByDate;
    }

    brokers.forEach((broker) => {
      //whether broker was selected in right panel
      const ogSelectionState = broker.selected;

      //set selected_cc for every broker that is selected by broker
      broker.selected_cc = selectedBrokers.includes(broker.sourceid);

      //set selected if selected_cc or previously selected
      broker.selected = broker.selected_cc || ogSelectionState;

      //deselect brokers not in cc if if hideNonCcBrokersInGrid is checked(true)
      if (hideNonCcBrokersInGrid) {
        broker.selected = broker.selected_cc;
      }

      //feature logging
      if (ogSelectionState !== broker.selected) {
        this.logSourceSelectionChange(broker);
      }
    });

    //todo: turn on cc if necessary [revisit when sourcepanel refresh is done ]
    //turn on meaning on the computed sources in source meta. set custom to selected
  };

  /**
   * writes/updates user's settings OTHER THAN broker section onto MEData object
   * @param prefs
   */
  updateRestOnMeData = (prefs) => {
    const sourcePanel = window.MEData.panels.find(
      (panel) => panel.name.toLowerCase() === "sources",
    );

    const {
      includeNonCurrentBrokers,
      includeNonentitledCurrent,
      includeNonentitledNoncurrent,
      showOnlyCurrent,
      sortColumn,
      sortDirection,
      dateCustomConsensus,
      relativeDays,
      customConsensusMode,
      customConsensusDateType,
      customConsensusBrokerType,
    } = prefs;

    //clear these variables first then assign if needed
    sourcePanel.date_custom_consensus = "";
    sourcePanel.relative_days = null;

    if (customConsensusMode.toLowerCase() === "bydate") {
      sourcePanel.date_custom_consensus = dateCustomConsensus;
      sourcePanel.relative_days = relativeDays;
      sourcePanel.inc_non_current_brokers = Number(!includeNonCurrentBrokers);
    } else {
      sourcePanel.selected_cc_not_entitled = includeNonentitledCurrent;
      sourcePanel.selected_excl_cc_not_entitled = includeNonentitledNoncurrent;
      sourcePanel.show_only_current_cc = showOnlyCurrent;
      sourcePanel.show_only_current = showOnlyCurrent;
      sourcePanel.sort_column = sortColumn;
      sourcePanel.sort_order = sortDirection
        ? sortDirection.split("end")[0]
        : "desc";
    }
    sourcePanel.clear_dimension = true;
    sourcePanel.custom_consensus_mode = customConsensusMode;
    sourcePanel.custom_consensus_date_type = customConsensusDateType;
    sourcePanel.excl_reaffirm_broker_consensus =
      customConsensusBrokerType === "reaffirm" ? true : "";
    sourcePanel["default-future"] = "Custom";
  };

  logSourceSelectionChange = (broker: BrokerItem) => {
    const feature = `OPTIONS_SOURCES_${broker.name.toUpperCase()}`;
    const extras = {
      VALUE: broker.selected ? "ON" : "OFF",
      URL: window.location.href,
      UTCTIME: this.appService.getCurrentUTCTime(),
    };

    this.ccService.logUserAction(feature, extras);
  };

  logRelativeDays = (prefsFromModal: NgModel) => {
    const mode = prefsFromModal.customConsensusMode;
    const relDays = prefsFromModal.relativeDays;
    if (mode !== "byDate" || !relDays) {
      return;
    }

    const feature = "OPTIONS_REVISION_DAYS";
    const extras = {
      VALUE: relDays,
      URL: window.location.href,
      UTCTIME: this.appService.getCurrentUTCTime(),
    };

    this.ccService.logUserAction(feature, extras);
  };

  logCustomConsensusApply = () => {
    const sourcePanel = this.ccService.getMEDataPanel(window.MEData, "sources");
    const computed = sourcePanel.values.filter((p) => p.name === "computed")[0];
    const custom = computed.values.filter((p) => p.name === "Custom")[0];

    const feature1 = "OPTIONS_SOURCES_CUSTOM";
    const extras1 = {
      VALUE: "ON",
      URL: window.location.href,
      UTCTIME: this.appService.getCurrentUTCTime(),
    };

    const feature2 = "OPTIONS_SOURCES_FOCUS";
    const extras2 = {
      VALUE: "Custom",
      URL: window.location.href,
      UTCTIME: this.appService.getCurrentUTCTime(),
    };

    //log only if cc was previously not applied
    if (!custom.selected) {
      this.ccService.logUserAction(feature1, extras1);
      this.ccService.logUserAction(feature2, extras2);
    }
  };
}
